/**
 * 路由
 *
 * 守卫做三件事：
 *  1. 未登录访问受保护页面 → 跳登录页，并把原目标记下来（登录后回跳）
 *  2. 已登录访问登录页 → 跳首页（避免「登录后仍停在登录页」）
 *  3. 角色不足 → 跳首页（前端这层只是为了不显示没用的入口，真正的鉴权在服务端）
 */
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { isLoggedIn, isSuperAdmin } from '../stores/session'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginPage.vue'),
    meta: { public: true, title: '登录' }
  },
  {
    path: '/',
    component: () => import('../layouts/ConsoleLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('../views/DashboardPage.vue'),
        meta: { title: '概览' }
      },
      {
        path: 'moderation',
        name: 'moderation',
        component: () => import('../views/ModerationQueuePage.vue'),
        meta: { title: '审核队列' }
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('../views/ReportsPage.vue'),
        meta: { title: '举报工单' }
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('../views/UsersPage.vue'),
        // 两种角色都能查用户；**封禁按钮**在页面内按角色隐藏，服务端另有 requireRole 兜底
        meta: { title: '用户管理' }
      },
      {
        path: 'topics',
        name: 'topics',
        component: () => import('../views/TopicsPage.vue'),
        meta: { title: '话题配置' }
      },
      {
        path: 'admins',
        name: 'admins',
        component: () => import('../views/AdminsPage.vue'),
        // 仅超级管理员。审核员看不到入口，直接输 URL 也会被守卫挡回首页
        meta: { title: '管理员账号', superAdminOnly: true }
      },
      {
        path: 'account',
        name: 'account',
        component: () => import('../views/AccountPage.vue'),
        meta: { title: '我的账号' }
      }
    ]
  },
  {
    // 兜底：未匹配的路径回首页。
    // 不加这一条的话，输错 URL 会渲染出一个空白页（Vue Router 的默认行为）
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

export const router = createRouter({
  // 用 hash 模式：管理后台部署在子路径下时不需要额外的 Nginx rewrite 规则，
  // 刷任何页面都不会 404
  history: createWebHashHistory(),
  routes
})

router.beforeEach((to) => {
  if (to.meta.public) {
    // 已登录还去登录页 → 回首页
    if (to.name === 'login' && isLoggedIn.value) return { name: 'dashboard' }
    return true
  }

  if (!isLoggedIn.value) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.superAdminOnly && !isSuperAdmin.value) {
    return { name: 'dashboard' }
  }

  return true
})

router.afterEach((to) => {
  const title = typeof to.meta.title === 'string' ? to.meta.title : ''
  document.title = title ? `${title} · 颗绘管理后台` : '颗绘管理后台'
})
